import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { OnyxDatePipe, OnyxWidgetBoxComponent } from '@onyx/angular';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { InvoiceType } from '../../../../../common/enums/invoice-type';
import { OrderStatus } from '../../../common/enums/order-status';
import { OrderHelper } from '../../../common/helpers/order.helper';
import type { Order } from '../../../common/interfaces/order';

@Component({
  selector: 'app-order-modal-finished-section-main-widget',
  imports: [OnyxWidgetBoxComponent, TranslatePipe],
  templateUrl: './order-modal-finished-section-main-widget.component.html',
  styleUrl: './order-modal-finished-section-main-widget.component.scss',
})
export class OrderModalFinishedSectionMainWidgetComponent {
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly orderHelper = inject(OrderHelper);

  public readonly order = input.required<Order>();

  protected readonly I18N = 'orders.orderModal';

  protected widgetData = computed(() => {
    const order = this.order();
    const { payment, status } = order;
    const paymentDelay = this.orderHelper.getPaymentDelay(order);
    const paymentColor = paymentDelay?.isPast ? 'red' : 'gray';

    const {
      collectiveInvoiceIssueFrom,
      contractorInvoice,
      contractorInvoiceDocuments,
    } = payment;

    switch (status.value) {
      case OrderStatus.COMPLETING_DOCUMENTS:
        return {
          heading: collectiveInvoiceIssueFrom
            ? 'labels.collectiveInvoiceIssueFrom'
            : 'labels.invoiceType',
          content: collectiveInvoiceIssueFrom
            ? this.datePipe.transform(collectiveInvoiceIssueFrom, 'date')
            : `${DictionaryCode.INVOICE_TYPE}.${contractorInvoice.defaultType}`,
        };

      case OrderStatus.INVOICE_TO_ISSUE: {
        if (payment.contractorInvoice.defaultType === InvoiceType.COLLECTIVE) {
          return {
            heading: 'labels.collectiveInvoiceIssueFrom',
            content: this.datePipe.transform(
              collectiveInvoiceIssueFrom,
              'date',
            ),
          };
        }

        const { shippingType } = contractorInvoiceDocuments;
        return {
          heading: `${this.I18N}.documentsCompleted`,
          content: OrderHelper.hasRequiredDocuments(shippingType)
            ? this.datePipe.transform(
                this.order().timestamps.invoiceToIssueAt!,
                'date-time',
              )
            : 'labels.notRequired',
        };
      }

      case OrderStatus.WAITING_FOR_PAYMENT:
      case OrderStatus.PARTIALLY_PAID:
      case OrderStatus.PAID: {
        const isPaid = status.value === OrderStatus.PAID;
        const date = isPaid
          ? order.timestamps.paidAt!
          : payment.invoice!.paymentTerm;

        return {
          heading: isPaid ? `${this.I18N}.paidDate` : 'labels.paymentTerm',
          content: this.datePipe.transform(date, 'date'),
          color: paymentColor,
          isPast: paymentDelay?.isPast,
          delay: paymentDelay?.days,
        } as const;
      }

      default:
        return null;
    }
  });
}
